// HeroCoverage — widget compatto di verifica copertura embedded nel page-hero
// di /offerte-casa. Due stati: form (Comune > Via > Civico) e done (chips on/off
// + tech raccomandata + reset). Usa lo stesso api.* reale del CoverageWizard.

import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router'
import { Icons } from './Icons'
import { CovSelect, AddressRescueModal } from './CoverageWizard'
import { CoverageVerifying, COVERAGE_VERIFY_MIN_MS } from './CoverageVerifying'
import { api, type Comune, type Via, type Civico, type CoverageResult, type CoverageLinea } from '@/lib/api'
import { useCoverageHealth } from '@/hooks/useCoverageHealth'
import { writeCoverage, coverageFromResult, lineaDistanceNote } from '@/lib/checkout'
import { trackEvent } from '@/lib/analytics'

export type TechKey = 'ftth' | 'fttc' | 'fwa'

export interface CoverageVM {
  address: string
  avail: Record<TechKey, boolean>
  recommended: TechKey
  // Disponibilità banda garantita FTTH+ (solo Business). Per ora la deriviamo
  // dalla presenza di FTTH; in futuro andra' agganciata a un campo dedicato del backend.
  garantita?: boolean
  // Rete (fornitore) della PRIMA linea attivabile per ciascuna tecnologia, nell'ordine
  // del payload (rete propria Spadhausen in cima): usato per mostrare la rete reale
  // dell'indirizzo invece del fornitore di listino.
  fornitore?: Partial<Record<TechKey, string>>
  // Velocità reale attivabile (download/upload) della stessa linea, scomposta in valore+unità:
  // per mostrare i numeri reali dell'indirizzo invece del massimo di listino. Assente se la
  // tecnologia non è attivabile (fallback al listino).
  down?: Partial<Record<TechKey, { v: string; u: string }>>
  up?: Partial<Record<TechKey, { v: string; u: string }>>
  // Download reale in Mbps (stesso dato di `down`, non arrotondato/formattato): serve a
  // confrontare con soglie di listino (es. gating dell'upgrade FTTH 10 Gigabit).
  downMbps?: Partial<Record<TechKey, number>>
  // Nota distanza (cabinet FTTC / BTS FWA su rete Spadhausen) per la modale esito.
  dist?: Partial<Record<TechKey, string>>
}

const LABELS: Record<TechKey, string> = { ftth: 'FTTH', fttc: 'FTTC', fwa: 'FWA' }

// Mbps -> { valore, unità } (es. 1000 -> {v:'1',u:'Gbps'}, 127 -> {v:'127',u:'Mbps'}).
export function speedParts(mbps: number): { v: string; u: string } {
  if (mbps >= 1000) {
    const g = mbps / 1000
    return { v: Number.isInteger(g) ? String(g) : g.toFixed(1).replace('.', ','), u: 'Gbps' }
  }
  return { v: String(mbps), u: 'Mbps' }
}

// Inverso di speedParts: { valore, unità } -> Mbps (es. {v:'2.5',u:'Gbps'} -> 2500).
export function specMbps(sv: { v: string; u: string }): number {
  const n = parseFloat(sv.v.replace(',', '.'))
  return sv.u === 'Gbps' ? n * 1000 : n
}

// Mascheramento rete (scelta commerciale, hardcoded): la rete TIM è sempre presentata
// come FiberCop. Applicato all'unico punto dove il fornitore è mostrato all'utente.
function maskFornitore(f: string | undefined): string | undefined {
  if (!f) return f
  return /^tim$/i.test(f.trim()) ? 'FiberCop' : f
}

// Riscrive i numeri di velocità di LISTINO presenti in un testo (descrizioni) con la
// velocità REALE attivabile all'indirizzo, quando c'è copertura. Gestisce sia la forma
// breve ("10 Gbps") sia quella estesa ("10 Gigabit"), e il separatore decimale . o ,.
export function dynamizeSpeeds(
  text: string,
  specs: { down: { v: string; u: string }; up: { v: string; u: string } },
  coverage: CoverageVM | null,
  key: TechKey,
): string {
  if (!coverage) return text
  const long = (u: string) => (u === 'Gbps' ? 'Gigabit' : u === 'Mbps' ? 'Megabit' : u)
  const sub = (t: string, l: { v: string; u: string }, r?: { v: string; u: string }) => {
    if (!r) return t
    const numPat = l.v.split(/[.,]/).map((p) => p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('[.,]')
    return t
      .replace(new RegExp(`\\b${numPat}\\s+${l.u}\\b`, 'g'), `${r.v} ${r.u}`)
      .replace(new RegExp(`\\b${numPat}\\s+${long(l.u)}\\b`, 'g'), `${r.v} ${long(r.u)}`)
  }
  return sub(sub(text, specs.down, coverage.down?.[key]), specs.up, coverage.up?.[key])
}

function buildVM(req: { comune: Comune; via: Via; civico: Civico }, result: CoverageResult): CoverageVM {
  const isAttivabile = (tech: string) =>
    result.linee.some((l) => l.tecnologia.toUpperCase() === tech && l.stato === 'Attivabile')
  const avail: Record<TechKey, boolean> = {
    ftth: isAttivabile('FTTH'),
    fttc: isAttivabile('FTTC') || isAttivabile('VULA'),
    fwa: isAttivabile('FWA'),
  }
  // Prima linea attivabile (ordine del payload, rete propria in cima) per ciascuna tech:
  // ne ricaviamo la rete reale (fornitore) e la velocità reale attivabile all'indirizzo.
  const lineOf = (techs: string[]): CoverageLinea | undefined =>
    result.linee.find((l) => techs.includes(l.tecnologia.toUpperCase()) && l.stato === 'Attivabile')
  const lines: Record<TechKey, CoverageLinea | undefined> = {
    ftth: lineOf(['FTTH']),
    fttc: lineOf(['FTTC', 'VULA']),
    fwa: lineOf(['FWA']),
  }
  const fornitore: Partial<Record<TechKey, string>> = {
    ftth: maskFornitore(lines.ftth?.fornitore),
    fttc: maskFornitore(lines.fttc?.fornitore),
    fwa: maskFornitore(lines.fwa?.fornitore),
  }
  const down: Partial<Record<TechKey, { v: string; u: string }>> = {
    ftth: lines.ftth && speedParts(lines.ftth.velocitaDownloadMbps),
    fttc: lines.fttc && speedParts(lines.fttc.velocitaDownloadMbps),
    fwa: lines.fwa && speedParts(lines.fwa.velocitaDownloadMbps),
  }
  const up: Partial<Record<TechKey, { v: string; u: string }>> = {
    ftth: lines.ftth && speedParts(lines.ftth.velocitaUploadMbps),
    fttc: lines.fttc && speedParts(lines.fttc.velocitaUploadMbps),
    fwa: lines.fwa && speedParts(lines.fwa.velocitaUploadMbps),
  }
  const downMbps: Partial<Record<TechKey, number>> = {
    ftth: lines.ftth?.velocitaDownloadMbps,
    fttc: lines.fttc?.velocitaDownloadMbps,
    fwa: lines.fwa?.velocitaDownloadMbps,
  }
  const dist: Partial<Record<TechKey, string>> = {
    ftth: lines.ftth ? lineaDistanceNote(lines.ftth) ?? undefined : undefined,
    fttc: lines.fttc ? lineaDistanceNote(lines.fttc) ?? undefined : undefined,
    fwa: lines.fwa ? lineaDistanceNote(lines.fwa) ?? undefined : undefined,
  }
  const recommended: TechKey = avail.ftth ? 'ftth' : avail.fttc ? 'fttc' : avail.fwa ? 'fwa' : 'ftth'
  const address = `${req.via.nome} ${req.civico.numero}, ${req.comune.nome}`
  return { address, avail, recommended, garantita: avail.ftth, fornitore, down, up, downMbps, dist }
}

interface Props {
  coverage: CoverageVM | null
  onResult: (vm: CoverageVM) => void
  onReset: () => void
  variant?: 'casa' | 'business'
}

export function HeroCoverage({ coverage, onResult, onReset, variant = 'casa' }: Props) {
  const [comune, setComune] = useState<Comune | null>(null)
  const [via, setVia] = useState<Via | null>(null)
  const [civico, setCivico] = useState<Civico | null>(null)
  const [verifyingAddr, setVerifyingAddr] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  // Esito in modale subito dopo la verifica (non quando coverage arriva da fuori).
  const [showResult, setShowResult] = useState(false)
  const [rescue, setRescue] = useState(false)
  const health = useCoverageHealth()
  const verifying = verifyingAddr !== null
  const submitRef = useRef<HTMLButtonElement>(null)
  const complete = !!(comune && via && civico)
  const fonte = variant === 'business' ? 'hero-business' : 'hero-casa'

  // Indirizzo completo → focus sul bottone Verifica: Enter avvia la verifica senza mouse.
  useEffect(() => { if (complete) submitRef.current?.focus() }, [complete])

  const reset = () => {
    setComune(null); setVia(null); setCivico(null); setError(null)
  }
  const handleReset = () => { reset(); onReset() }

  const canSubmit = comune && via && civico && !verifying
  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!comune || !via || !civico) return
    setError(null)
    setVerifyingAddr(`${via.nome} ${civico.numero}, ${comune.nome}`)
    try {
      // L'overlay copre la fetch reale; l'attesa minima garantisce che l'animazione
      // completi anche su risposte velocissime (vedi CoverageVerifying).
      const [result] = await Promise.all([
        api.verifyCoverage({ comune, via, civico }),
        new Promise((r) => setTimeout(r, COVERAGE_VERIFY_MIN_MS)),
      ])
      onResult(buildVM({ comune, via, civico }, result))
      setShowResult(true)
      // Condivide la verifica col funnel /verifica-copertura (bridge cookie).
      writeCoverage(coverageFromResult(result, comune, via, civico))
      const attiva = result.linee.find((l) => l.stato === 'Attivabile')
      trackEvent('verifica_copertura', {
        esito: attiva ? 'coperta' : 'non_coperta',
        tecnologia: attiva?.tecnologia ?? '',
        comune: comune.nome,
        fonte,
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore verifica')
      trackEvent('verifica_copertura', { esito: 'errore', comune: comune.nome, fonte })
    } finally {
      setVerifyingAddr(null)
    }
  }

  if (coverage) {
    return (
      <>
        {showResult && (
          <HeroResultModal
            coverage={coverage}
            variant={variant}
            fonte={fonte}
            onClose={() => setShowResult(false)}
            onEdit={() => { setShowResult(false); handleReset() }}
          />
        )}
        <aside className="hero-cov is-done">
        <div className="hero-cov-top">
          <span className="hero-cov-ic"><Icons.check /></span>
          <div>
            <strong>{variant === 'business' ? 'Sede coperta' : 'Indirizzo coperto'}</strong>
            <span>{coverage.address}</span>
          </div>
        </div>
        <div className="hero-cov-chips">
          {(['ftth', 'fttc', 'fwa'] as TechKey[]).map((k) => (
            <span key={k} className={`cov-chip ${coverage.avail[k] ? 'on' : 'off'}`}>
              {coverage.avail[k] ? <Icons.check /> : <Icons.x />} {LABELS[k]}
            </span>
          ))}
          {variant === 'business' && (
            <span className={`cov-chip ${coverage.garantita ? 'on' : 'off'}`}>
              {coverage.garantita ? <Icons.check /> : <Icons.x />} FTTH+
            </span>
          )}
        </div>
        <p className="hero-cov-note">
          {variant === 'business' ? (
            <>
              Disponibile da te: <strong>{LABELS[coverage.recommended]}</strong>
              {coverage.garantita ? ' · banda garantita FTTH+ attivabile.' : ' · FTTH+ su fattibilità.'}
            </>
          ) : (
            <>Pagina filtrata sulle offerte <strong>{LABELS[coverage.recommended]}</strong>, disponibili da te.</>
          )}
        </p>
        <button
          type="button"
          className="cta-ghost on-dark"
          onClick={handleReset}
          style={{ width: '100%', justifyContent: 'center' }}
        >
          <Icons.pin style={{ width: 14, height: 14 }} /> Modifica indirizzo
        </button>
        </aside>
      </>
    )
  }

  const unavailable = health === 'unavailable'

  return (
    <aside className="hero-cov">
      <CoverageVerifying target={verifyingAddr ? { address: verifyingAddr } : null} />
      <div className="hero-cov-label">
        <span className="hero-cov-dot" /> Verifica la copertura
      </div>
      <p className="hero-cov-sub">
        {variant === 'business'
          ? 'Scopri quali tecnologie raggiungono la tua sede — è opzionale.'
          : 'Scopri quali tecnologie arrivano al tuo indirizzo — è opzionale.'}
      </p>
      <form className="cov-wizard" onSubmit={submit}>
        {(comune || via || civico) && (
          <div className="cov-crumbs">
            {comune && (
              <button type="button" className="cov-crumb" onClick={() => { setComune(null); setVia(null); setCivico(null) }}>
                <span className="cov-crumb-label">Comune</span>
                <span className="cov-crumb-val">{comune.nome}</span>
                <span className="cov-crumb-x">×</span>
              </button>
            )}
            {via && (
              <button type="button" className="cov-crumb" onClick={() => { setVia(null); setCivico(null) }}>
                <span className="cov-crumb-label">Via</span>
                <span className="cov-crumb-val">{via.nome}</span>
                <span className="cov-crumb-x">×</span>
              </button>
            )}
            {civico && (
              <button type="button" className="cov-crumb" onClick={() => setCivico(null)}>
                <span className="cov-crumb-label">Civico</span>
                <span className="cov-crumb-val">{civico.numero}</span>
                <span className="cov-crumb-x">×</span>
              </button>
            )}
          </div>
        )}
        <div className="cov-step-row">
          {!comune && (
            <CovSelect<Comune>
              key="step-comune"
              placeholder="Comune"
              fetch={api.searchComuni}
              minChars={2}
              getLabel={(c) => (c.provincia ? `${c.nome} (${c.provincia})` : c.nome)}
              onSelect={setComune}
              disabled={unavailable}
              track={{ campo: 'comune', fonte }}
              onNotFound={() => setRescue(true)}
            />
          )}
          {comune && !via && (
            <CovSelect<Via>
              key="step-via"
              placeholder="Indirizzo / via"
              fetch={(q) => api.searchVie(comune.egonId, q)}
              minChars={3}
              getLabel={(v) => (v.frazione ? `${v.nome} — ${v.frazione}` : v.nome)}
              onSelect={setVia}
              autoFocus
              track={{ campo: 'via', fonte }}
              onNotFound={() => setRescue(true)}
            />
          )}
          {comune && via && !civico && (
            <CovSelect<Civico>
              key="step-civico"
              placeholder="Numero civico"
              fetch={(q) => api.searchCivici(via.egonId, q)}
              minChars={1}
              getLabel={(c) => c.numero}
              onSelect={setCivico}
              autoFocus
              track={{ campo: 'civico', fonte }}
              onNotFound={() => setRescue(true)}
            />
          )}
          <button ref={submitRef} type="submit" className="cta-grad" disabled={!canSubmit || unavailable}>
            {verifying ? 'Verifica…' : 'Verifica'} <Icons.arrow />
          </button>
        </div>
        {unavailable && (
          <p className="cov-unavailable-msg">Verifica di copertura al momento non disponibile</p>
        )}
        {error && (
          <p style={{ color: 'var(--sh-magenta)', fontSize: 12, marginTop: 0 }}>⚠️ {error}</p>
        )}
      </form>
      <div className="hero-cov-or">oppure esplora liberamente le offerte ↓</div>
      {rescue && (
        <AddressRescueModal
          contesto={`Indirizzo non trovato (widget ${fonte}): ${[comune?.nome, via?.nome].filter(Boolean).join(', ') || 'nessuna selezione'}`}
          onClose={() => setRescue(false)}
        />
      )}
    </aside>
  )
}

// ============================================================
// Modale esito verifica — appare subito dopo la verifica e invita
// a proseguire con la configurazione del pacchetto (riusa .cov-modal-*).
// ============================================================
function HeroResultModal({ coverage, variant, fonte, onClose, onEdit }: {
  coverage: CoverageVM
  variant: 'casa' | 'business'
  fonte: string
  onClose: () => void
  onEdit: () => void
}) {
  const navigate = useNavigate()
  const covered = coverage.avail.ftth || coverage.avail.fttc || coverage.avail.fwa
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])
  const reco = coverage.recommended
  const down = coverage.down?.[reco]
  const up = coverage.up?.[reco]
  const dist = coverage.dist?.[reco]
  // Portalata su <body>: dentro .hero-cov il backdrop-filter crea uno stacking
  // context che intrappola il fixed sotto le barre sticky (vedi CovSelect).
  return createPortal(
    <div className="cov-modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="cov-modal" role="dialog" aria-modal="true">
        <button type="button" className="cov-modal-close" onClick={onClose} aria-label="Chiudi">×</button>
        {covered ? (
          <>
            <div className="cov-modal-icon">
              {reco === 'fwa' ? <Icons.signal style={{ width: 32, height: 32 }} /> : <Icons.bolt style={{ width: 32, height: 32 }} />}
            </div>
            <div className="cov-modal-eyebrow">Buone notizie</div>
            <h3 className="cov-modal-title">
              Disponibile: <span className="g">{LABELS[reco]}{down ? ` ${down.v} ${down.u}` : ''}</span>
            </h3>
            <div className="cov-modal-addr">{coverage.address}</div>
            {dist && <div className="cov-modal-dist">{dist}</div>}
            {(down || up) && (
              <div className="cov-modal-specs">
                {down && <div className="cov-spec"><div className="cov-spec-k">Download</div><div className="cov-spec-v">{down.v} {down.u}</div></div>}
                {up && <div className="cov-spec"><div className="cov-spec-k">Upload</div><div className="cov-spec-v">{up.v} {up.u}</div></div>}
              </div>
            )}
            <div className="cov-modal-actions">
              <button className="cta-grad" type="button" onClick={() => { trackEvent('copertura_continua', { fonte, esito: 'coperta' }); onClose() }}>
                Configura il tuo pacchetto <Icons.arrow />
              </button>
              <button className="cta-ghost" type="button" onClick={onEdit}>Cambia indirizzo</button>
            </div>
          </>
        ) : (
          <>
            <div className="cov-modal-icon"><Icons.pin style={{ width: 32, height: 32 }} /></div>
            <div className="cov-modal-eyebrow">Non ancora coperto</div>
            <h3 className="cov-modal-title">
              {variant === 'business'
                ? <>La sede <span className="g">non è ancora coperta</span></>
                : <>L'indirizzo <span className="g">non è ancora coperto</span></>}
            </h3>
            <div className="cov-modal-addr">{coverage.address}</div>
            <p style={{ marginTop: 8, color: 'rgba(255,255,255,0.7)' }}>
              Inviaci comunque la richiesta: valutiamo una soluzione su misura e ti ricontattiamo.
            </p>
            <div className="cov-modal-actions">
              <button className="cta-grad" type="button" onClick={() => { trackEvent('copertura_continua', { fonte, esito: 'non_coperta' }); navigate('/verifica-copertura') }}>
                Invia comunque la richiesta <Icons.arrow />
              </button>
              <button className="cta-ghost" type="button" onClick={onEdit}>Cambia indirizzo</button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  )
}
